// src/shared/lib/fetchProfileData.ts

import { FxIdWebFeaturesProfileGetResponse } from "@app/shared/api/gen";
import { queries } from "@app/shared/api/Queries";
import { queryClient } from "@app/shared/api/QueryClient.ts";
import { authTokenExists } from "@shared/api/Auth.ts";
import log from "loglevel";

/**
 * @fileoverview Вспомогательные функции для работы с API профиля вне React-компонентов.
 */

/**
 * Асинхронно получает данные профиля пользователя.
 *
 * Эта функция является оберткой над `queryClient.fetchQuery`. Она использует кэш React Query:
 * - Если данные есть в кэше и они не устарели (staleTime: Infinity), вернет их из кэша.
 * - Если для этого queryKey уже выполняется запрос, дождется его завершения и вернет результат.
 * - Если данных нет, выполнит сетевой запрос.
 *
 * Предназначена для использования в не-реактивном коде (например, в SDK).
 * Требует токена аутентификации для выполнения запроса.
 */
export const fetchProfileData = async (
	gameSystemName: string | undefined,
	forceRefetch = false
): Promise<FxIdWebFeaturesProfileGetResponse> => {
	if (!authTokenExists()) {
		// Это критическая ошибка программиста, поэтому бросаем исключение.
		throw new Error("Failed profile data fetch: authToken is not available.");
	}

	log.debug("Fetching profile data via helper for game: %s...", gameSystemName);

	const queryDetails = queries.profile.get({ Game: gameSystemName });

	// Если требуется принудительное обновление, инвалидируем соответствующий запрос в кэше.
	// Это заставит fetchQuery выполнить сетевой запрос, даже если в кэше есть свежие данные.
	if (forceRefetch) {
		log.debug("Forcing refetch for profile data with key: %o", queryDetails.queryKey);
		await queryClient.invalidateQueries(queryDetails);
	}

	try {
		// Используем fetchQuery, т.к. он атомарно выполняет всю необходимую логику:
		// 1. Проверяет кэш.
		// 2. Подписывается на уже идущий запрос, если он есть.
		// 3. Запускает новый запрос, если необходимо.
		// Это решает проблему "гонки состояний" между хуком и этой функцией.
		log.debug("Fetching profile data with query key", queryDetails.queryKey);
		const profile = await queryClient.fetchQuery(queryDetails);

		// "Защитный барьер": Убеждаемся, что данные действительно были получены.
		// fetchQuery может вернуть undefined, если queryFn так сделает (что маловероятно с нашим API).
		if (!profile) {
			throw new Error("Profile data resolved to a falsy value after fetchQuery.");
		}

		log.debug("Profile data successfully fetched via helper for game: %s", gameSystemName);
		return profile;
	} catch (error) {
		// Логируем ошибку и пробрасываем ее дальше, чтобы вызывающий код мог ее обработать.
		log.error("An error occurred while fetching profile data in helper:", error);
		throw error;
	}
};
